// =================================================================================================
// © 2021 Shrewd Apps
// ALL RIGHTS RESERVED.

import React, { Suspense } from "react";
import { PropTypes } from "prop-types";
import { useParams } from "react-router-dom";
import { Container, Row, Col } from "reactstrap";
import { Breadcrumb, BreadcrumbItem } from "reactstrap";
import { Helmet } from "react-helmet";

import PageErrorBoundary from "global/errors/PageErrorBoundary";
import ErrorLoadingSection from "global/errors/ErrorLoadingSection";
import LoadingSpinner from "global/loading/LoadingSpinner";
import LinkToHomePage from "common/links/LinkToHomePage";
import LinkToTournamentSearchResults from "common/links/LinkToTournamentSearchResults";
import TournamentPanel from "./TournamentPanel";

import { constructGetTournamentUrl } from "api";
import { tournamentSchema } from "apiSchemas";
import { fetchResource } from "global/loading/fetchResource";
import { formatDate } from "global/utilities/format";
import { getFormatDisplayNames } from "common/resources/formatNames";
import { getCurrentDateIso8601 } from "global/utilities/Date";

// -------------------------------------------------------------------------------------------------
// Page that shows details of a single tournament including standings.
// REVIEW
export default function TournamentPage() {
	const { tournamentId } = useParams();

	// FUTURE: Read optional price data source name
	//const location = useLocation();
	//const urlFilterParameters = parseUrlSearchParameters(location.search);

	return (
		<PageErrorBoundary>
			<TournamentPageContents tournamentId={tournamentId} />
		</PageErrorBoundary>
	);
}

// -------------------------------------------------------------------------------------------------
export function TournamentPageContents({ tournamentId, displayOptions }) {
	const getTournamentUrl = constructGetTournamentUrl(tournamentId);
	const tournamentResource = fetchResource(getTournamentUrl);

	return (
		<Container>
			<Suspense fallback={<LoadingSpinner />}>
				<TournamentPageContentsLoader
					tournamentResource={tournamentResource}
					displayOptions={displayOptions} />
			</Suspense>
		</Container>
	);
}

TournamentPageContents.propTypes = {
	tournamentId: PropTypes.string.isRequired,
	displayOptions: PropTypes.object
};

// -------------------------------------------------------------------------------------------------
function TournamentPageContentsLoader({ tournamentResource, displayOptions }) {
	const tournament = tournamentResource.data;
	const tournamentState = tournamentResource.state;

	return (
		<>
			{tournament &&
				<Row>
					<Col>
						<Breadcrumbs
							tournament={tournament} />
					</Col>
				</Row>
			}
			{tournament &&
				<Row>
					<Col>
						<TournamentPanel
							tournament={tournament}
							displayOptions={displayOptions} />
					</Col>
				</Row>
			}
			{tournamentState && tournamentState.isError &&
				<ErrorLoadingSection errorMessage={tournamentState.errorMessage} />
			}
		</>
	);
}

TournamentPageContentsLoader.propTypes = {
	tournamentResource: PropTypes.object,
	displayOptions: PropTypes.object
};

// -------------------------------------------------------------------------------------------------
function Breadcrumbs({ tournament }) {
	const tournamentName = tournament && tournament.overall && tournament.overall.tournamentName;
	const tournamentDate = formatDate(tournament.overall.date);
	const formatName = getFormatDisplayNames(tournament.overall.formatIds);
	const rootDomain = `${window.location.protocol}//${window.location.hostname}`;
	const title = tournamentName + " | Magic the Gathering | MTG Top Decks";
	const shortTitle = tournamentName;
	const description = `Magic the Gathering tournament result for ${formatName} on ${tournamentDate}`;
	const keywords = "Magic the Gathering, cards, archetypes, metagame, decks, tournaments";
	const date = getCurrentDateIso8601();
	const breadcrumbsLdJson = {
		"@context": "https://schema.org",
		"@type": "BreadcrumbList",
		"itemListElement:": [{
			"@type": "ListItem",
			"position": 1,
			"name": "Home",
			"item": rootDomain
		},
		{
			"@type": "ListItem",
			"position": 2,
			"name": "Tournaments",
			"item": rootDomain + "/tournaments"
		},
		{
			"@type": "ListItem",
			"position": 3,
			"name": tournamentName
		}]
	};

	const pageLdJson = {
		"@context": "https://schema.org",
		"@type": "Article",
		"description": description,
		"keywords": keywords,
		"dateCreated": date,
		"dateModified": date,
		"headline": title,
		"image": [
			rootDomain + "/assets/logo.png"
		],
		"author": [{
			"@type": "Organization",
			"name": "MTG Top Decks",
			"url": rootDomain
		}]
	};

	return (
		<>
			<Helmet>
				<title>{title}</title>
				<meta name="description" content={description} />
				<meta property="og:title" content={shortTitle} />
				<meta property="og:description" content={description} />
				<meta property="og:type" content="article" />
				<meta property="og:url" content={window.location.href} />
				<meta property="og:modified_time" content={date} />
				<meta property="og:site_name" content="MTG Top Decks" />
				<script type="application/ld+json">{JSON.stringify(breadcrumbsLdJson)}</script>
				<script type="application/ld+json">{JSON.stringify(pageLdJson)}</script>
			</Helmet>

			<Breadcrumb>
				<BreadcrumbItem><LinkToHomePage>Home</LinkToHomePage></BreadcrumbItem>
				<BreadcrumbItem><LinkToTournamentSearchResults>Tournaments</LinkToTournamentSearchResults></BreadcrumbItem>
				<BreadcrumbItem active>{tournamentName}</BreadcrumbItem>
			</Breadcrumb>
		</>
	);
}

Breadcrumbs.propTypes = {
	tournament: PropTypes.shape(tournamentSchema).isRequired
};